import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  RoomTypeAvailability,
  OccupancyInput,
  RoomSuggestion,
  RoomCombination,
  CategoryOptionDto,
} from "../../types";
import { getProfitRoomAvailability } from "../../services/api";

interface ProfitRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  offerDates: {
    checkIn: string;
    checkOut: string;
  };
  availableOptions: CategoryOptionDto[];
  onSelectRooms: (combinations: RoomCombination[]) => void;
}

const ProfitRoomModal: React.FC<ProfitRoomModalProps> = ({
  isOpen,
  onClose,
  offerDates,
  availableOptions,
  onSelectRooms,
}) => {
  const [phase, setPhase] = useState<1 | 2>(1);
  const [occupancyInput, setOccupancyInput] = useState<OccupancyInput>({
    totalPersons: 0,
    pairsCount: 0,
    singlesCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<RoomSuggestion[]>([]);

  // Calculate singles when pairs change
  useEffect(() => {
    const singles = occupancyInput.totalPersons - occupancyInput.pairsCount * 2;
    if (singles >= 0) {
      setOccupancyInput((prev) => ({ ...prev, singlesCount: singles }));
    }
  }, [occupancyInput.totalPersons, occupancyInput.pairsCount]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhase(1);
      setOccupancyInput({ totalPersons: 0, pairsCount: 0, singlesCount: 0 });
      setError(null);
      setSuggestions([]);
    }
  }, [isOpen]);

  const validateOccupancy = () => {
    if (occupancyInput.totalPersons <= 0) {
      return "Liczba osób musi być większa od 0";
    }
    if (occupancyInput.pairsCount < 0) {
      return "Liczba par nie może być ujemna";
    }
    if (occupancyInput.singlesCount < 0) {
      return "Zbyt dużo par dla podanej liczby osób";
    }
    return null;
  };

  const handleSearchAvailability = async () => {
    const validationError = validateOccupancy();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getProfitRoomAvailability({
        checkIn: offerDates.checkIn,
        checkOut: offerDates.checkOut,
        single: occupancyInput.singlesCount,
        pairs: occupancyInput.pairsCount,
      });

      // Filter rooms that have mapping in our system
      const mappedRooms = response.roomTypes.filter(
        (room: RoomTypeAvailability) =>
          availableOptions.some(
            (option) => option.profitroomOfferId === room.roomId
          )
      );

      // Generate suggestions
      const roomSuggestions = generateRoomSuggestions(
        mappedRooms,
        occupancyInput.pairsCount,
        occupancyInput.singlesCount
      );

      setSuggestions(roomSuggestions);
      setPhase(2);
    } catch (err: any) {
      console.error("Failed to fetch Profitroom availability:", err);
      setError(
        err.response?.data?.message ||
          "Nie udało się pobrać dostępności pokoi. Spróbuj ponownie."
      );
    } finally {
      setLoading(false);
    }
  };

  const selectMidRangePricing = (offers: any[]) => {
    if (!offers || offers.length === 0) return null;

    // Sort by price ascending
    const sortedOffers = [...offers].sort(
      (a, b) => a.pricing.amount - b.pricing.amount
    );

    // Select from middle 60% of price range (skip bottom 20% and top 20%)
    if (sortedOffers.length >= 5) {
      const skipCount = Math.floor(sortedOffers.length * 0.2);
      const midRangeOffers = sortedOffers.slice(skipCount, -skipCount);
      return midRangeOffers[0]; // Cheapest from mid-range
    }

    // If less than 5 offers, take the middle one
    const middleIndex = Math.floor(sortedOffers.length / 2);
    return sortedOffers[middleIndex];
  };

  const generateRoomSuggestions = (
    availableRooms: RoomTypeAvailability[],
    pairsNeeded: number,
    singlesNeeded: number
  ): RoomSuggestion[] => {
    if (!availableRooms.length) return [];

    const suggestions: RoomSuggestion[] = [];
    const totalRoomsNeeded = pairsNeeded + singlesNeeded;

    // Helper function to create room combination
    const createCombination = (
      roomSelections: Array<{
        room: RoomTypeAvailability;
        count: number;
        offer: any;
      }>
    ): RoomCombination[] => {
      return roomSelections.map((selection) => {
        const categoryOption = availableOptions.find(
          (opt) => opt.profitroomOfferId === selection.room.roomId
        );

        return {
          roomType: selection.room.roomId,
          roomName: selection.room.roomName,
          count: selection.count,
          pricePerRoom: selection.offer.pricing.amount,
          totalPrice: selection.offer.pricing.amount * selection.count,
          categoryOptionId: categoryOption?.id || "",
        };
      });
    };

    // Strategy 1: Try single room type that can accommodate all
    for (const room of availableRooms) {
      if (room.availableCount >= totalRoomsNeeded) {
        const offer = selectMidRangePricing(room.offers);
        if (offer) {
          const combination = createCombination([
            { room, count: totalRoomsNeeded, offer },
          ]);
          const totalPrice = combination.reduce(
            (sum, c) => sum + c.totalPrice,
            0
          );

          suggestions.push({
            combination,
            totalRooms: totalRoomsNeeded,
            totalPrice,
            efficiency: 100, // Perfect room utilization
          });
        }
      }
    }

    // Strategy 2: Optimize for room types (doubles for pairs, singles for singles)
    const doubleRooms = availableRooms.filter((room) => room.maxOccupancy >= 2);
    const singleRooms = availableRooms.filter(
      (room) => room.maxOccupancy === 1
    );

    if (doubleRooms.length > 0 && singleRooms.length > 0) {
      // Try to use doubles for pairs and singles for singles
      for (const doubleRoom of doubleRooms) {
        for (const singleRoom of singleRooms) {
          const availableDoubles = Math.min(
            doubleRoom.availableCount,
            pairsNeeded
          );
          const availableSingles = Math.min(
            singleRoom.availableCount,
            singlesNeeded
          );

          if (availableDoubles + availableSingles >= totalRoomsNeeded * 0.8) {
            // At least 80% coverage
            const doubleOffer = selectMidRangePricing(doubleRoom.offers);
            const singleOffer = selectMidRangePricing(singleRoom.offers);

            if (doubleOffer && singleOffer) {
              const roomSelections = [];

              if (availableDoubles > 0) {
                roomSelections.push({
                  room: doubleRoom,
                  count: availableDoubles,
                  offer: doubleOffer,
                });
              }

              if (availableSingles > 0) {
                roomSelections.push({
                  room: singleRoom,
                  count: availableSingles,
                  offer: singleOffer,
                });
              }

              const combination = createCombination(roomSelections);
              const totalPrice = combination.reduce(
                (sum, c) => sum + c.totalPrice,
                0
              );
              const efficiency =
                ((availableDoubles + availableSingles) / totalRoomsNeeded) *
                100;

              suggestions.push({
                combination,
                totalRooms: availableDoubles + availableSingles,
                totalPrice,
                efficiency,
              });
            }
          }
        }
      }
    }

    // Strategy 3: Mixed combinations of available rooms
    // This is more complex but provides backup options
    for (let i = 0; i < availableRooms.length; i++) {
      for (let j = i + 1; j < availableRooms.length; j++) {
        const room1 = availableRooms[i];
        const room2 = availableRooms[j];

        const offer1 = selectMidRangePricing(room1.offers);
        const offer2 = selectMidRangePricing(room2.offers);

        if (offer1 && offer2) {
          // Try different distributions
          const maxRoom1 = Math.min(
            room1.availableCount,
            Math.ceil(totalRoomsNeeded * 0.7)
          );
          const maxRoom2 = Math.min(
            room2.availableCount,
            totalRoomsNeeded - maxRoom1
          );

          if (maxRoom1 + maxRoom2 >= totalRoomsNeeded) {
            const combination = createCombination([
              { room: room1, count: maxRoom1, offer: offer1 },
              { room: room2, count: maxRoom2, offer: offer2 },
            ]);

            const totalPrice = combination.reduce(
              (sum, c) => sum + c.totalPrice,
              0
            );
            const efficiency = ((maxRoom1 + maxRoom2) / totalRoomsNeeded) * 100;

            suggestions.push({
              combination,
              totalRooms: maxRoom1 + maxRoom2,
              totalPrice,
              efficiency,
            });
          }
        }
      }
    }

    // Sort by efficiency first, then by price
    return suggestions
      .filter((s) => s.totalRooms >= totalRoomsNeeded) // Only complete solutions
      .sort(
        (a, b) => b.efficiency - a.efficiency || a.totalPrice - b.totalPrice
      )
      .slice(0, 3); // Top 3 suggestions
  };

  const handleSelectSuggestion = (suggestion: RoomSuggestion) => {
    onSelectRooms(suggestion.combination);
    onClose();
  };

  const handleBackToPhase1 = () => {
    setPhase(1);
    setError(null);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      data-profitroom-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            🏨 Dodaj pokoje z Profitroom
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {phase === 1 && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                Określ liczbę osób i sposób ich rozmieszczenia w pokojach.
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Liczba osób łącznie
                </label>
                <input
                  type="number"
                  min="1"
                  value={occupancyInput.totalPersons || ""}
                  onChange={(e) =>
                    setOccupancyInput((prev) => ({
                      ...prev,
                      totalPersons: parseInt(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="np. 15"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Liczba par (2 osoby na pokój)
                </label>
                <input
                  type="number"
                  min="0"
                  max={Math.floor(occupancyInput.totalPersons / 2)}
                  value={occupancyInput.pairsCount || ""}
                  onChange={(e) =>
                    setOccupancyInput((prev) => ({
                      ...prev,
                      pairsCount: parseInt(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="np. 6"
                />
              </div>

              {/* Auto-calculated display */}
              {occupancyInput.totalPersons > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-sm text-blue-800 space-y-1">
                    <div className="flex items-center">
                      <span className="mr-2">👥</span>
                      <span>
                        Pary: {occupancyInput.pairsCount} pokoi (po 2 osoby) ={" "}
                        {occupancyInput.pairsCount * 2} osób
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="mr-2">👤</span>
                      <span>
                        Jedynki: {occupancyInput.singlesCount} pokoi (po 1
                        osobie) = {occupancyInput.singlesCount} osób
                      </span>
                    </div>
                    <div className="border-t border-blue-200 pt-2 mt-2 font-semibold">
                      Potrzebne pokoje:{" "}
                      {occupancyInput.pairsCount + occupancyInput.singlesCount}
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-sm text-red-800">{error}</div>
                </div>
              )}

              <button
                onClick={handleSearchAvailability}
                disabled={loading || occupancyInput.totalPersons <= 0}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sprawdzam dostępność..." : "Sprawdź dostępność"}
              </button>
            </div>
          )}

          {phase === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Dostępne pokoje</h3>
                <button
                  onClick={handleBackToPhase1}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  ← Zmień parametry
                </button>
              </div>

              {suggestions.length > 0 ? (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-3">
                      💡 Automatyczne sugestie (średnia półka cenowa)
                    </h4>

                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="border border-green-300 rounded-lg p-3 mb-2 cursor-pointer hover:bg-green-100 transition-colors"
                        onClick={() => handleSelectSuggestion(suggestion)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            {suggestion.combination.map((combo, comboIndex) => (
                              <div
                                key={comboIndex}
                                className="text-sm text-green-800"
                              >
                                <span className="font-medium">
                                  {combo.count}x
                                </span>{" "}
                                {combo.roomName}{" "}
                                <span className="text-gray-600">
                                  ({combo.pricePerRoom} zł/pokój)
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="text-right ml-4">
                            <div className="font-semibold text-green-800">
                              {suggestion.totalPrice} zł
                            </div>
                            <div className="text-xs text-gray-600">
                              {suggestion.totalRooms} pokoi
                            </div>
                            <div className="text-xs text-gray-500">
                              Efektywność: {Math.round(suggestion.efficiency)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="mb-2">😔</div>
                  <div>Brak dostępnych pokoi spełniających kryteria</div>
                  <div className="text-sm mt-1">
                    Sprawdź czy pokoje mają skonfigurowane mapowanie w panelu
                    administracyjnym
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-end space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Anuluj
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ProfitRoomModal;
